<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <div class="element-box mt-4">
            <div class="row mt-2">
              <!-- 1. Einzelcoaching -->
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>

              <!-- 2. Gruppenmaßnahmen -->
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  icon="fa-users"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>

              <!-- 3. Soziotherapie -->
              <CoachingButton
                  :enabled="companySettings?.socioTherapyCoachingEnabled"
                  route-name="socioTherapyCoachingNewAndActiveList"
                  icon="fa-hand-holding-heart"
                  coaching-type="socioTherapy"
                  label="Soziotherapie"/>

              <!-- 4. Betreutes Wohnen -->
              <CoachingButton
                  :enabled="companySettings?.assistedLivingCoachingEnabled"
                  route-name="assistedLivingCoachingNewAndActiveList"
                  icon="fa-house-chimney-user"
                  coaching-type="assistedLiving"
                  label="Betreutes Wohnen"/>

              <!-- 5. Private Weiterbildung -->
              <CoachingButton
                  :enabled="companySettings?.privateLearningCoachingEnabled"
                  route-name="privateLearningCoachingNewAndActiveList"
                  icon="fa-graduation-cap"
                  coaching-type="privateLearning"
                  label="Private Weiterbildung"/>
            </div>
          </div>

          <h6 class="mt-5">{{ $t('general.active_customers') }}</h6>
          <div class="row g-4 mt-1">
            <!-- 1. Einzelcoaching -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.privateCoachingEnabled">
              <CoachingCardInfo
                  label="Einzelcoaching"
                  type="private"
                  :value="infoData?.countActiveCoaching?.counts?.privateCoaching"
              />
            </div>

            <!-- 2. Gruppenmaßnahmen -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.groupCoachingEnabled">
              <CoachingCardInfo
                  label="Gr.-Maßnahmen"
                  type="group"
                  :value="infoData?.countActiveCoaching?.counts?.groupCoaching"
              />
            </div>

            <!-- 3. Soziotherapie -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.socioTherapyCoachingEnabled">
              <CoachingCardInfo
                  label="Soziotherapie"
                  type="socioTherapy"
                  :value="infoData?.countActiveCoaching?.counts?.socioTherapyCoaching"
              />
            </div>

            <!-- 4. Private Weiterbildung -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.privateLearningCoachingEnabled">
              <CoachingCardInfo
                  label="Private Weiterbildung"
                  type="privateLearning"
                  :value="countPrivateLearningCoaching"
              />
            </div>

            <!-- 5. Betreutes Wohnen -->
            <div class="col-lg-3 col-md-6 col-sm-12"
                 v-if="companySettings?.assistedLivingCoachingEnabled">
              <CoachingCardInfo
                  label="Betreutes Wohnen"
                  type="assistedLiving"
                  :value="countAssistedLivingCoaching"
              />
            </div>
          </div>

          <div v-if="companySettings?.locationEnabled" class="mt-4">
            <h6 class="section-subtitle mb-3">
              Übersicht Standorte
            </h6>

            <div class="row g-4">
              <!-- 1. Interne Standorte -->
              <div class="col-lg-3 col-md-6 col-sm-12">
                <CoachingCardInfo
                    label="Interne Standorte"
                    type="internalLocation"
                    :value="infoData?.countInternalOffices"
                />
              </div>

              <!-- 2. Externe Standorte -->
              <div class="col-lg-3 col-md-6 col-sm-12">
                <CoachingCardInfo
                    label="Externe Standorte"
                    type="externalLocation"
                    :value="infoData?.countExternalOffices"
                />
              </div>

              <!-- 3. Sonstige Standorte -->
              <div class="col-lg-3 col-md-6 col-sm-12">
                <CoachingCardInfo
                    label="Sonstige Standorte"
                    type="otherLocation"
                    :value="infoData?.countOtherOffices"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box pie mt-2">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts
              :key="seriesActiveCoach.length"
              width="100%"
              type="donut"
              :options="chartOptions"
              :series="seriesActiveCoach"/>
        </div>

        <div class="element-box">
          <h6>Standorte nach Bundesländern</h6>
          <div v-for="user of infoData?.countOfficeByFederalState" v-bind:key="user?.name"
               class="d-flex justify-content-between">
            <span>{{ user?.federalState }}</span>
            <span>{{ user?.count }}</span>
          </div>
        </div>

        <div class="element-box mt-2" v-if="infoData?.todayBirthdays?.length">
          <Birthday :birthdays="infoData?.todayBirthdays" />
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardHeadMarketingService from '@/api/Dashboard/HeadMarketing';
import Birthday from '@/components/Birthday.vue';
import CoachingButton from '@/components/button/CoachingButton.vue';
import CoachingCardInfo from '@/components/card/CoachingCardInfo.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import { ErrorToast } from '@/components/VueToast';
import { getDonutColorsByCompany, optionsChartDonut } from '@/shared/utils/apex';
import { piniaCompanySettingsStore } from '@/store/piniaCompanySettingsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import { computed, onMounted, ref } from 'vue';
import VueApexCharts from 'vue3-apexcharts';
import {initAppIfAuthenticated} from "@/shared/functions";

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const chartOptions      = ref(optionsChartDonut);
const seriesActiveCoach = ref([]);

const infoData = ref({});

const countPrivateLearningCoaching = computed(() =>
    (infoData?.value?.countActiveCoaching?.counts?.privateLearningCoaching ?? 0) +
    (infoData?.value?.countNewCoaching?.counts?.privateLearningCoaching ?? 0),
);

const countAssistedLivingCoaching = computed(() =>
    (infoData?.value?.countActiveCoaching?.counts?.assistedLivingCoaching ?? 0) +
    (infoData?.value?.countNewCoaching?.counts?.assistedLivingCoaching ?? 0),
);

async function getInfoData() {
  try {
    const { data } = await dashboardHeadMarketingService.getInfoData();
    infoData.value = data.data;

    const counts = data.data.countActiveCoaching?.counts ?? {};
    const series = [
      counts.privateCoaching ?? 0,
      counts.groupCoaching ?? 0,
      counts.socioTherapyCoaching ?? 0,
      countPrivateLearningCoaching.value,
      countAssistedLivingCoaching.value,
    ];
    const countActiveCoachingTotal = series.reduce((sum, value) => sum + value, 0);

    seriesActiveCoach.value = series;
    chartOptions.value      = {
      ...chartOptions.value,
      colors: getDonutColorsByCompany(companySettings.value?.companyKey),
      labels: [
        `Einzelcoaching ${series[0]}`,
        `Gruppenmaßnahmen ${series[1]}`,
        `Soziotherapie ${series[2]}`,
        `Private Weiterbildung ${series[3]}`,
        `Betreutes Wohnen ${series[4]}`,
      ],

      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'left',
        fontSize: '11px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        textColor: '#64748b',
        itemMargin: {
          horizontal: 6,
          vertical: 2
        },
        markers: {
          width: 8,
          height: 8,
          radius: 12,
          offsetX: -3
        }
      },

      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => countActiveCoachingTotal.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await initAppIfAuthenticated()
  await getInfoData();
});
</script>

<style scoped>
::v-deep(.pie .apexcharts-legend) {
  display: block !important;
}
</style>
